/**
 * siteLayout.ts — how a website's repository is laid out for blog posts.
 *
 * The CMS was first built for the DemandHub site, whose files and index JSON are described in
 * docs/ARCHITECTURE.md (section 4). Other Astro sites keep their posts differently, so each site
 * stores a layout: `{ kind: "demandhub" }` (built in, stored as NULL) or `{ kind: "blog", ... }`,
 * which describes a plain Astro content collection:
 *
 *   - where the post files live and how they are named (flat `slug.md` or `slug/index.md`),
 *   - the public address of a post,
 *   - the frontmatter key names the site's schema uses (null = the site has no such field),
 *   - whether the body is Markdown or HTML, and where pictures live.
 *
 * Everything here is pure (no I/O) so the worker, the detector and the admin all use the same rules.
 */
import { z } from "zod";

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

/** A path inside the repository: no leading slash, no "..", no backslashes, no empty parts. */
const repoDir = z
  .string()
  .min(1)
  .max(200)
  .refine((v) => !v.startsWith("/") && !v.endsWith("/") && !v.includes("\\") && !v.split("/").some((p) => p === "" || p === "." || p === ".."), {
    message: 'Use a folder path inside the repository such as "src/content/blog" (no leading or trailing slash, no "..").',
  });

/** A frontmatter key as written in the site's content schema, e.g. "title" or "seoTitle". */
const fmKey = z.string().min(1).max(60).regex(/^[A-Za-z_][A-Za-z0-9_-]*$/, "Use the field name exactly as the site's content schema spells it.");

export const blogFieldsSchema = z.object({
  title: fmKey,
  /** A shorter title for search results; written only when it differs from the title. */
  seoTitle: fmKey.nullable(),
  /** The short summary / meta description. */
  description: fmKey.nullable(),
  date: fmKey,
  dateModified: fmKey.nullable(),
  /** The main picture at the top of a post. */
  image: fmKey.nullable(),
  /** Category names. The first is the CMS category; any further names are kept as tags so nothing is lost. */
  categories: fmKey.nullable(),
  tags: fmKey.nullable(),
  author: fmKey.nullable(),
});
export type BlogFields = z.infer<typeof blogFieldsSchema>;

export const blogLayoutSchema = z.object({
  kind: z.literal("blog"),
  /** Folder that holds the post files, e.g. "src/content/blog". */
  folder: repoDir,
  /** "flat": `<folder>/<slug>.md`. "folder": `<folder>/<slug>/index.md`. */
  fileShape: z.enum(["flat", "folder"]),
  /** Public address prefix, e.g. "/blog" (a post lives at `<prefix>/<slug>`). */
  urlPrefix: z
    .string()
    .min(1)
    .max(100)
    .regex(/^\/[A-Za-z0-9_\-/]*$/, 'Start with a slash, for example "/blog".'),
  trailingSlash: z.boolean(),
  /** "markdown": the body is written as Markdown. "html": the file body is HTML (the editor still writes Markdown). */
  bodyFormat: z.enum(["markdown", "html"]),
  /** How the date is written: "2026-07-19" (iso_day) or "July 19, 2026" (long). */
  dateStyle: z.enum(["iso_day", "long"]),
  /** Folder that holds pictures, e.g. "src/assets/blog" or "public/blog". */
  imageFolder: repoDir,
  /** How the site's schema stores categories: a list of names (default) or a single name. */
  categoriesShape: z.enum(["list", "single"]).default("list"),
  /** "flat": all pictures in the folder. "per_slug": one subfolder per post. */
  imageMode: z.enum(["flat", "per_slug"]),
  /** When true the site's schema requires a main picture, so a post cannot be published without one. */
  imageRequired: z.boolean(),
  fields: blogFieldsSchema,
});
export type BlogLayout = z.infer<typeof blogLayoutSchema>;

export const demandhubLayoutSchema = z.object({ kind: z.literal("demandhub") });

/** What a site stores. NULL in the database means the built-in DemandHub layout. */
export const siteLayoutSchema = z.discriminatedUnion("kind", [demandhubLayoutSchema, blogLayoutSchema]);
export type SiteLayout = z.infer<typeof siteLayoutSchema>;

/** The layout a site actually uses (NULL / missing = DemandHub's). */
export function effectiveLayout(layout: SiteLayout | null | undefined): SiteLayout {
  return layout ?? { kind: "demandhub" };
}

export function isBlogLayout(layout: SiteLayout | null | undefined): layout is BlogLayout {
  return !!layout && layout.kind === "blog";
}

/** Parses the JSON stored in `sites.content_layout`. Anything unreadable is treated as "not set" rather than crashing a publish. */
export function parseStoredLayout(raw: string | null | undefined): SiteLayout | null {
  if (!raw) return null;
  try {
    const parsed = siteLayoutSchema.safeParse(JSON.parse(raw));
    if (!parsed.success || parsed.data.kind === "demandhub") return null;
    return parsed.data;
  } catch {
    return null;
  }
}

/** What to write to `sites.content_layout`: NULL for DemandHub's built-in layout, JSON otherwise. */
export function layoutToStored(layout: SiteLayout | null | undefined): string | null {
  return isBlogLayout(layout) ? JSON.stringify(layout) : null;
}

// ---------------------------------------------------------------------------
// Paths
// ---------------------------------------------------------------------------

/** Content types a blog layout can hold. Case studies and ebooks have their own page designs on DemandHub only. */
export const BLOG_CONTENT_TYPES = ["article"] as const;

export function blogSupportsType(type: string): boolean {
  return (BLOG_CONTENT_TYPES as readonly string[]).includes(type);
}

/** Repo path of a post's file. */
export function blogFilePath(layout: BlogLayout, slug: string): string {
  return layout.fileShape === "folder" ? `${layout.folder}/${slug}/index.md` : `${layout.folder}/${slug}.md`;
}

/** Public path of a post, e.g. "/blog/my-post" (with a trailing slash when the site uses one). */
export function blogPublicPath(layout: BlogLayout, slug: string): string {
  const prefix = layout.urlPrefix.replace(/\/+$/, "");
  return `${prefix}/${slug}${layout.trailingSlash ? "/" : ""}`;
}

/** The slug of a post file inside the layout's folder, or null if the path is not a post file of this layout. */
export function blogSlugFromPath(layout: BlogLayout, repoPath: string): string | null {
  const escaped = layout.folder.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = layout.fileShape === "folder" ? new RegExp(`^${escaped}/([^/]+)/index\\.md$`) : new RegExp(`^${escaped}/([^/]+)\\.md$`);
  const m = re.exec(repoPath);
  return m ? m[1]! : null;
}

/** Repo path for a picture belonging to a post (`slug` is ignored in flat mode). */
export function blogImagePath(layout: BlogLayout, slug: string | undefined, filename: string): string {
  return layout.imageMode === "per_slug" && slug ? `${layout.imageFolder}/${slug}/${filename}` : `${layout.imageFolder}/${filename}`;
}

/** `public/...` pictures are served from the site root, so they are referenced as "/..." rather than relatively. */
const PUBLIC_DIR = "public/";

/** The reference to write inside a post file so that it points at `toRepoPath`. */
export function relativeRef(fromFile: string, toRepoPath: string): string {
  if (toRepoPath.startsWith(PUBLIC_DIR)) return `/${toRepoPath.slice(PUBLIC_DIR.length)}`;
  const from = fromFile.split("/").slice(0, -1);
  const to = toRepoPath.split("/");
  let i = 0;
  while (i < from.length && i < to.length - 1 && from[i] === to[i]) i++;
  const up = from.length - i;
  return [...Array<string>(up).fill(".."), ...to.slice(i)].join("/");
}

/** The inverse of relativeRef: the repo path a reference inside `fromFile` points at, or null for external / unresolvable references. */
export function resolveRef(fromFile: string, ref: string): string | null {
  if (!ref || /^[a-z][a-z0-9+.-]*:/i.test(ref) || ref.startsWith("//") || ref.startsWith("#")) return null;
  if (ref.startsWith("/")) return `${PUBLIC_DIR}${ref.slice(1)}`;
  const parts = fromFile.split("/").slice(0, -1);
  for (const seg of ref.split("/")) {
    if (seg === "" || seg === ".") continue;
    if (seg === "..") {
      if (parts.length === 0) return null;
      parts.pop();
    } else parts.push(seg);
  }
  return parts.join("/");
}

/** Every picture a post body references, as repo paths (external addresses are skipped). Handles `![](x)` and `<img src="x">`. */
export function blogBodyImagePaths(layout: BlogLayout, slug: string, body: string): string[] {
  const file = blogFilePath(layout, slug);
  const refs = new Set<string>();
  for (const m of body.matchAll(/!\[[^\]]*\]\(([^)\s]+)(?:\s+"[^"]*")?\)/g)) refs.add(m[1]!);
  for (const m of body.matchAll(/<img\b[^>]*?\bsrc=["']([^"']+)["'][^>]*>/gi)) refs.add(m[1]!);
  const out = new Set<string>();
  for (const ref of refs) {
    const p = resolveRef(file, ref);
    if (p) out.add(p);
  }
  return [...out];
}

/** The reference to put in the editor's body for a picture at `repoPath` (what the editor inserts after an upload). */
export function blogBodyImageRef(layout: BlogLayout, slug: string, repoPath: string): string {
  return relativeRef(blogFilePath(layout, slug), repoPath);
}

// ---------------------------------------------------------------------------
// Rules
// ---------------------------------------------------------------------------

export interface BlogPublishCheckInput {
  type: string;
  title: string;
  metaDescription?: string | null;
  description?: string | null;
  metaTitle?: string | null;
  bodyMarkdown: string;
  authorId?: string | null;
  titleImageUrl?: string | null;
  publishDate?: string | null;
  dateModified?: string | null;
}

const blank = (v: string | null | undefined) => v === null || v === undefined || v.trim() === "";

/**
 * Plain-language reasons a post cannot be published to a blog-layout site. Same contract as
 * getContentPublishBlockers (schemas.ts), which calls this when the site has a blog layout.
 */
export function blogPublishBlockers(layout: BlogLayout, item: BlogPublishCheckInput): string[] {
  const out: string[] = [];
  if (!blogSupportsType(item.type)) {
    out.push("This site can only publish articles.");
    return out;
  }
  if (blank(item.title)) out.push("Add a title.");
  if (blank(item.bodyMarkdown)) out.push("Write the text before publishing.");
  if (layout.fields.description && blank(item.metaDescription) && blank(item.description)) out.push("Add a short summary.");
  if (layout.fields.author && !item.authorId) out.push("Choose an author.");
  if (layout.fields.image && layout.imageRequired && blank(item.titleImageUrl)) {
    out.push("Add a main picture. This site needs one.");
  }
  const unreadable = (v: string | null | undefined) => !blank(v) && Number.isNaN(new Date(v as string).getTime());
  if (unreadable(item.publishDate)) out.push("Pick a real date for the publish date.");
  if (unreadable(item.dateModified)) out.push("Pick a real date for the date modified.");
  return out;
}

/** A one-line plain-words summary of a layout, for the connect screen. */
export function describeLayout(layout: SiteLayout | null | undefined): string {
  if (!isBlogLayout(layout)) return "DemandHub layout (articles, case studies, ebooks with index files)";
  const shape = layout.fileShape === "folder" ? "one folder per post" : "one file per post";
  const body = layout.bodyFormat === "html" ? "HTML body" : "Markdown body";
  return `Blog posts in ${layout.folder} (${shape}, ${body}), published at ${blogPublicPath(layout, "your-post")}`;
}

// ---------------------------------------------------------------------------
// Detection result (what the connect screen shows)
// ---------------------------------------------------------------------------

export const layoutQuestionSchema = z.object({
  id: z.string(),
  label: z.string(),
  options: z.array(z.object({ value: z.string(), label: z.string() })).min(1),
});
export type LayoutQuestion = z.infer<typeof layoutQuestionSchema>;

export const layoutDetectionSchema = z.object({
  /** ok: a layout was worked out and can be used. needs_answers: one or more questions must be answered first. unsupported: the site cannot be published to yet. */
  status: z.enum(["ok", "needs_answers", "unsupported"]),
  layout: blogLayoutSchema.nullable(),
  summary: z.string(),
  checks: z.array(z.object({ ok: z.boolean(), label: z.string(), detail: z.string() })),
  questions: z.array(layoutQuestionSchema),
  /** Plain reasons the site cannot be used as it is (status "unsupported"). */
  blockers: z.array(z.string()),
  /** Things worth knowing that do not stop publishing. */
  notes: z.array(z.string()),
});
export type LayoutDetection = z.infer<typeof layoutDetectionSchema>;
